import React from 'react';
import { withTranslation } from 'react-i18next';
import useUser from '@behavior/init/getUserV2';
import { Grid } from 'semantic-ui-react';
import connect from 'react-redux/es/connect/connect';
import { Formik } from 'formik';
import { compose } from 'redux';
import { AccordionWrapper, InputWrapper } from 'react-lib/dist/components-es';
import GridColumnInputWrapper from '../common/GridColumnInputWrapper';
import ButtonWrapper from '../common/ButtonWrapper.tsx';
import { locationsActions } from '../../behavior/locations';
import { initSelectors } from '../../behavior/init';

function LinkifiedText({ text = '' }) {
  const urlRegex = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
  const parts = text.split(urlRegex);
  const nodes = parts.reduce((acc, part, idx) => {
    if (!part) { 
      return acc; 
    }
    if (idx % 2 === 1) {
      const urlText = part;
      const href = /^https?:\/\//i.test(urlText) ? urlText : `https://${urlText}`;
      acc.push(
        <a
          key={`lnk-${acc.length}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#2185d0', textDecoration: 'underline' }}
        >
          {urlText}
        </a>
      );
      return acc;
    }

    acc.push(<React.Fragment key={`txt-${acc.length}`}>{part}</React.Fragment>);
    return acc;
  }, []);

  return <div>{nodes}</div>;
}

function ReadonlyBox({ value = '' }) {
  return (
    <div
      className="readOnlyTextArea"
      style={{
        whiteSpace: 'pre-wrap',
        border: '1px solid rgba(34,36,38,.15)',
        borderRadius: 4,
        padding: '0.67857143em 1em',
        minHeight: 80,
        background: '#f9fafb',
        color: 'rgba(0,0,0,.87)',
        overflowWrap: 'anywhere',
        cursor: 'text',
      }}
    >
      <LinkifiedText text={value} />
    </div>
  );
}

function BImportantInformationContainer(props) {
  const { values, save } = props;
  const { user } = useUser();
  const [editMode, setEditMode] = React.useState(false);

  const canModifyImportantInfo =
    initSelectors.isFireBrigade(user) ||
    initSelectors.isChemicalSafetyExpert(user) ||
    initSelectors.isEnvironmentalExpert(user);

  const submitHandle = (info, actions) => {
    if (!editMode) {
      setEditMode(true);
      actions.setSubmitting(false);
      return;
    }
    setEditMode(false);
    const { id, importantInformation } = info;
    save({ id, importantInformation });
    actions.setSubmitting(false);
  };

  const handleCancel = () => setEditMode(false);

  const getContainerBody = () => (
    <Formik
      initialValues={values.location}
      enableReinitialize
      onSubmit={submitHandle}
      onReset={handleCancel}
      >
        {(formik) => (
          <Grid stackable columns={1}>
            {editMode ? (
              <GridColumnInputWrapper
                {...formik}
                value={formik.values.importantInformation || ''}
                name="importantInformation"
                render={(p) => <InputWrapper type="textArea" {...p} />}
              />
            ) : (
              <GridColumnInputWrapper
                {...formik}
                value={formik.values.importantInformation || ''}
                name="importantInformation"
                render={(p) => <ReadonlyBox value={p.value || ''} />}
              />
            )}

            {canModifyImportantInfo &&
              (editMode ? (
                <div className="building-flex-buttons">
                  <GridColumnInputWrapper
                    fieldClassName="importantInformationButtons"
                    {...formik}
                    render={() => (
                      <ButtonWrapper
                        id="importantInformationUpdateButton"
                        label="details.page.update.button"
                        primary
                        className="mini inputWiderButton right floated"
                        onClick={formik.handleSubmit}
                      />
                    )}
                  />
                  <GridColumnInputWrapper
                    fieldClassName="importantInformationButtons"
                    {...formik}
                    render={() => (
                      <ButtonWrapper
                        id="importantInformationCancelButton"
                        label="details.page.cancel.button"
                        color="red"
                        className="mini inputWiderButton right floated"
                        onClick={formik.handleReset}
                      />
                    )}
                  />
                </div>
              ) : (
                <GridColumnInputWrapper
                  fieldClassName="importantInformationButtons"
                  {...formik}
                  render={() => (
                    <ButtonWrapper
                      id="importantInformationEditButton"
                      label="details.page.edit.button"
                      primary
                      icon="edit"
                      className="mini inputWiderButton right floated"
                      onClick={formik.handleSubmit}
                    />
                  )}
                />
              ))}
          </Grid>
        )}
      </Formik>
  );
  

  return (
    <AccordionWrapper
      {...props}
      closable={false}
      accordionTitle="building.summary.page.important.information.container.title"
      tooltip="building.summary.page.important.information.container.important.info.tooltip"
      render={getContainerBody}
      className="containerReducedWhiteSpace"
    />
  );
}

const mapStateToProps = (state) => ({
  ...state.locations,
});

const mapDispatchToProps = (dispatch) => ({
  save: (saveDto) => dispatch(locationsActions.saveLocation(saveDto)),
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(BImportantInformationContainer);
